// Utilitarios das telas de Fiscalizacao mensal (RMA).
import axios from "axios";
import { registerBadgeSet } from "@/components/ui/badge-statuses";

// <ui-badge set="rma-item" :status="item.status">
registerBadgeSet("rma-item", {
  pending: ["Pendente", "neutral"],
  sent: ["Enviado", "info"],
  accepted: ["Conferido", "success"],
  returned: ["Devolvido", "danger"],
  not_applicable: ["Não se aplica", "purple"],
});

// <ui-badge set="rma-competence" :status="row.status">
registerBadgeSet("rma-competence", {
  not_opened: ["Não aberta", "neutral"],
  awaiting: ["Aguardando envio", "warning"],
  receiving: ["Recebendo", "info"],
  complete: ["Completo", "success"],
});

// Status de entrega dos e-mails (email_logs).
registerBadgeSet("rma-email", {
  queued: ["Na fila do provedor", "info"],
  delivered: ["Entregue", "success"],
  failed: ["Falhou", "danger"],
  rejected: ["Recusado", "danger"],
  error: ["Não enviado", "danger"],
});

export const ITEM_STATUS_LABELS = {
  pending: "Pendente",
  sent: "Enviado",
  accepted: "Conferido",
  returned: "Devolvido",
  not_applicable: "Não se aplica",
};

export const REVIEW_LABELS = {
  ok: "Aprovado",
  incomplete: "Incompleto",
  wrong: "Rejeitado",
};

export const EMAIL_KIND_LABELS = {
  rma_request: "Solicitação",
  rma_reminder: "Lembrete",
  rma_overdue: "Prazo encerrado",
  rma_item_returned: "Documento devolvido",
  rma_pending_reinformed: "Pendências reinformadas",
  rma_upload_digest: "Resumo ao responsável",
  rma_draft_review: "Aviso de revisão",
  rma_clarification: "Pedido de esclarecimento",
  rma_clarification_answered: "Esclarecimento respondido",
};

export function errorMessage(error, fallback = "Ocorreu um erro inesperado. Tente novamente.") {
  const content = error && error.response && error.response.data && error.response.data.content;
  if (content && content.error && content.error.message) return content.error.message;
  // 413 sem corpo da API: o proxy (Cloudflare) recusou o envio pelo tamanho.
  if (error && error.response && error.response.status === 413) return "O arquivo é maior que o limite de envio (200 MB por arquivo).";
  return fallback;
}

// O banco devolve carimbos em UTC como "YYYY-MM-DD HH:mm:ss".
export function parseUtc(value) {
  if (!value) return null;
  const text = String(value);
  const date = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(text) ? new Date(`${text.replace(" ", "T")}Z`) : new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(value) {
  const date = parseUtc(value);
  return date ? date.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" }) : "—";
}

// Data de calendario "YYYY-MM-DD" -> "DD/MM/YYYY".
export function formatDate(value) {
  if (!value) return "—";
  const text = String(value).slice(0, 10);
  return `${text.slice(8, 10)}/${text.slice(5, 7)}/${text.slice(0, 4)}`;
}

export function formatBytes(bytes) {
  const value = Number(bytes) || 0;
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
  if (value >= 1024) return `${Math.round(value / 1024)} KB`;
  return `${value} B`;
}

const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

// "2026-06" -> "junho/2026"
export function competenceLabel(key) {
  const match = String(key || "").match(/^(\d{4})-(\d{2})/);
  return match ? `${MONTHS[Number(match[2]) - 1]}/${match[1]}` : "";
}

// Mes anterior ao atual (Brasilia), no formato "YYYY-MM".
export function previousCompetenceKey(date = new Date()) {
  const parts = new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit" }).format(date).split("-");
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, "0")}`;
}

// Ultimos `count` meses ate o mes anterior, do mais recente para o mais antigo.
export function recentCompetenceKeys(count = 18) {
  const keys = [];
  let key = previousCompetenceKey();
  for (let i = 0; i < count; i += 1) {
    keys.push(key);
    const [year, month] = key.split("-").map(Number);
    key = month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, "0")}`;
  }
  return keys;
}

// Envio do arquivo para a API (multipart, campo `file`); a API repassa ao storage
// em stream. `onProgress` recebe { loaded, total, percent } dos bytes enviados.
// Sem timeout: um arquivo de 200 MB leva minutos em conexao lenta.
export function uploadFile(url, file, { onProgress, method = "post" } = {}) {
  const form = new FormData();
  form.append("file", file, file.name);
  return axios.request({
    url,
    method,
    data: form,
    timeout: 0,
    onUploadProgress: (event) => {
      if (!onProgress) return;
      const total = event.total || file.size || 0;
      const ratio = total ? Math.min(1, event.loaded / total) : 0;
      onProgress({ loaded: Math.round(ratio * (file.size || total)), total: file.size || total, percent: Math.floor(ratio * 100) });
    },
  });
}

export function formatExtensions(list) {
  return (list || []).map((ext) => String(ext).toUpperCase()).join(", ");
}

export function notifyError(vm, error) {
  vm.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 5000 });
}

export function notifyWarning(vm, message) {
  vm.$dialog.notify.warning(message, { position: "top-right", timeout: 5000 });
}

export function notifySuccess(vm, message) {
  vm.$dialog.notify.success(message, { position: "top-right", timeout: 2500 });
}

export function openUrl(url) {
  if (url) window.open(url, "_blank", "noopener");
}
